import { useTranslation } from 'react-i18next'
import { useExperimentManager } from 'services/ExperimentManager/ExperimentManager'
import { progressFor } from '../discountSteps'
import { getStepAppliedText, getStepNotMetText } from '../discountStepsText'
import { getMainGoals } from '../groupPricingToDiscountData'
import { useLastGroupPricing } from './useLastGroupPricing'

export type DiscountInfo = {
  type: 'buyover' | 'bundle' | 'percent'
  applied: boolean
  progress: number
  appliedText: string
  notMetText: string
}

// The cart's discount row. It covers only the main discount — shipping progress
// lives in the stepped progress bar.
export function useDiscountInfo(): DiscountInfo | null {
  const { t } = useTranslation()
  const experimentManager = useExperimentManager()
  const groupPricing = useLastGroupPricing()

  if (!groupPricing) return null

  const { currency } = groupPricing
  const goals = getMainGoals(groupPricing, () =>
    experimentManager.isEnabled('bundle-communication-v2')
  )
  if (!goals?.length) return null

  const activeGoal = goals.find((goal) => !goal.applied)
  // Tiers don't stack, so the ladder's top tier is the one whose discount counts.
  const highestGoal = goals[goals.length - 1]

  return {
    // A tiered buyover may open with an always-applied $0 percent goal; any
    // buyover goal still makes the whole discount a buyover.
    type: goals.some((goal) => goal.goal === 'buyover')
      ? 'buyover'
      : highestGoal.goal,
    applied: !activeGoal,
    progress: activeGoal ? progressFor(activeGoal) : 100,
    appliedText: getStepAppliedText(highestGoal, currency, t, {
      totalSavedCopyEnabled: highestGoal.goal === 'buyover' && !activeGoal,
    }),
    notMetText: getStepNotMetText(activeGoal ?? highestGoal, currency, t),
  }
}
